import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { addFavorite, fetchFavorites, removeFavorite } from "../lib/api";

const FavCtx = createContext(null);

const LS_KEY = "wd.favorites.anon";
const NOTIFY_PERM_KEY = "wd.notify.asked";

const requestNotifyPermission = () => {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (localStorage.getItem(NOTIFY_PERM_KEY)) return;
  if (Notification.permission === "default") {
    Notification.requestPermission().finally(() =>
      localStorage.setItem(NOTIFY_PERM_KEY, "1"),
    );
  } else {
    localStorage.setItem(NOTIFY_PERM_KEY, "1");
  }
};

const fireOnlineNotify = (fav) => {
  const name = fav?.server?.name || fav.server_id;
  toast.success(`${name} is back online`, {
    description: `Hop in — ${fav.server?.players ?? "?"}/${fav.server?.max_players ?? "?"} slots`,
    duration: 7000,
    action: {
      label: "View",
      onClick: () => {
        const url = `${window.location.origin}/?server=${encodeURIComponent(
          fav.server_id,
        )}&game=${encodeURIComponent(fav.game_id)}`;
        window.location.href = url;
      },
    },
  });
  try {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      new Notification("Favorite server back online", {
        body: `${name} — ${fav.server?.players ?? "?"}/${fav.server?.max_players ?? "?"} players`,
        tag: `wd-online-${fav.server_id}`,
      });
    }
  } catch { /* ignore */ }
};

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth();
  const authed = !!(user && user.user_id);
  const qc = useQueryClient();

  const [anonFavs, setAnonFavs] = React.useState(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
    } catch {
      return [];
    }
  });

  const { data: serverFavs = [] } = useQuery({
    queryKey: ["favorites"],
    queryFn: fetchFavorites,
    enabled: authed,
    staleTime: 15000,
    refetchInterval: 30000,
  });

  const addMut = useMutation({
    mutationFn: ({ server_id, game_id }) => addFavorite(server_id, game_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["favorites"] }),
  });

  const removeMut = useMutation({
    mutationFn: ({ server_id }) => removeFavorite(server_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["favorites"] }),
  });

  const favorites = authed
    ? serverFavs.map((f) => ({ server_id: f.server_id, game_id: f.game_id, server: f.server }))
    : anonFavs;

  const favSet = useMemo(() => new Set(favorites.map((f) => f.server_id)), [favorites]);
  const favIds = useMemo(() => favorites.map((f) => f.server_id), [favorites]);

  // --------------------------------------------------------------------
  // Offline → Online notifier
  // Tracks each favorite's online state. When transition offline→online
  // happens, fires a toast + browser Notification (if permitted).
  // --------------------------------------------------------------------
  const prevOnlineRef = useRef(null); // Map<server_id, boolean>
  useEffect(() => {
    const current = new Map();
    favorites.forEach((f) => {
      if (f.server && typeof f.server.online === "boolean") {
        current.set(f.server_id, f.server.online);
      }
    });
    // Skip the very first run — we don't have a baseline yet
    if (prevOnlineRef.current !== null) {
      favorites.forEach((f) => {
        if (!f.server) return;
        const wasOnline = prevOnlineRef.current.get(f.server_id);
        const isOnline = current.get(f.server_id);
        if (wasOnline === false && isOnline === true) {
          fireOnlineNotify(f);
        }
      });
    }
    prevOnlineRef.current = current;
  }, [favorites]);

  const isFavorite = (sid) => favSet.has(sid);

  const toggleFavorite = async (server) => {
    const sid = server.id;
    const gameId = server.game_id;
    requestNotifyPermission();
    if (authed) {
      if (favSet.has(sid)) await removeMut.mutateAsync({ server_id: sid });
      else await addMut.mutateAsync({ server_id: sid, game_id: gameId });
    } else {
      setAnonFavs((prev) => {
        const exists = prev.some((f) => f.server_id === sid);
        const next = exists
          ? prev.filter((f) => f.server_id !== sid)
          : [...prev, { server_id: sid, game_id: gameId, server }];
        localStorage.setItem(LS_KEY, JSON.stringify(next));
        return next;
      });
    }
  };

  // Sync anon favorites up on login (one-shot)
  useEffect(() => {
    if (!authed) return;
    const pending = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
    if (!pending.length) return;
    (async () => {
      for (const f of pending) {
        try {
          await addFavorite(f.server_id, f.game_id);
        } catch {
          /* ignore */
        }
      }
      localStorage.removeItem(LS_KEY);
      setAnonFavs([]);
      qc.invalidateQueries({ queryKey: ["favorites"] });
    })();
  }, [authed, qc]);

  return (
    <FavCtx.Provider value={{ favorites, favIds, isFavorite, toggleFavorite, authed }}>
      {children}
    </FavCtx.Provider>
  );
};

export const useFavorites = () => {
  const ctx = useContext(FavCtx);
  if (!ctx) throw new Error("useFavorites must be used inside FavoritesProvider");
  return ctx;
};
